/**
 * Edit Company Details Dialog for the customer settings tab.
 */

import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';

export interface CompanyEditForm {
  customer_code: string;
  registration_number: string;
  payment_terms: string;
  currency: string;
  annual_revenue: string;
  employee_count: string;
  year_established: string;
  primary_email: string;
  primary_phone: string;
  website: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface EditCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editForm: CompanyEditForm;
  setEditForm: React.Dispatch<React.SetStateAction<CompanyEditForm>>;
  saving: boolean;
  onSave: () => void;
}

export function EditCompanyDialog({
  open, onOpenChange, editForm, setEditForm, saving, onSave,
}: EditCompanyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Customer Details</DialogTitle>
          <DialogDescription>Update company information, contact details, and payment terms</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Customer Identification */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Customer Identification</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Customer Code</label>
                <input type="text" value={editForm.customer_code} onChange={(e) => setEditForm({ ...editForm, customer_code: e.target.value })} placeholder="e.g. CUST-001" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Company Number</label>
                <input type="text" value={editForm.registration_number} onChange={(e) => setEditForm({ ...editForm, registration_number: e.target.value })} placeholder="e.g. 12345678" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
            </div>
          </div>

          {/* Payment & Financial */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Payment & Financial</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Payment Terms</label>
                <input type="text" value={editForm.payment_terms} onChange={(e) => setEditForm({ ...editForm, payment_terms: e.target.value })} placeholder="e.g. Net 30, Net 60" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Currency</label>
                <select value={editForm.currency} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                  {[
                    { value: 'GBP', label: 'GBP - British Pound' },
                    { value: 'USD', label: 'USD - US Dollar' },
                    { value: 'EUR', label: 'EUR - Euro' },
                    { value: 'PLN', label: 'PLN - Polish Zloty' },
                    { value: 'CAD', label: 'CAD - Canadian Dollar' },
                    { value: 'AUD', label: 'AUD - Australian Dollar' },
                    { value: 'CHF', label: 'CHF - Swiss Franc' },
                    { value: 'SEK', label: 'SEK - Swedish Krona' },
                    { value: 'NOK', label: 'NOK - Norwegian Krone' },
                    { value: 'DKK', label: 'DKK - Danish Krone' },
                    { value: 'CZK', label: 'CZK - Czech Koruna' },
                    { value: 'JPY', label: 'JPY - Japanese Yen' },
                    { value: 'CNY', label: 'CNY - Chinese Yuan' },
                    { value: 'INR', label: 'INR - Indian Rupee' },
                  ].map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Annual Revenue</label>
                <input type="text" value={editForm.annual_revenue} onChange={(e) => setEditForm({ ...editForm, annual_revenue: e.target.value })} placeholder="e.g. 5000000" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Business Details</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Employee Count</label>
                <input type="number" value={editForm.employee_count} onChange={(e) => setEditForm({ ...editForm, employee_count: e.target.value })} placeholder="e.g. 250" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Year Established</label>
                <input type="number" value={editForm.year_established} onChange={(e) => setEditForm({ ...editForm, year_established: e.target.value })} placeholder="e.g. 1990" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Contact Information</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Email</label>
                <input type="email" value={editForm.primary_email} onChange={(e) => setEditForm({ ...editForm, primary_email: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Telephone</label>
                <input type="tel" value={editForm.primary_phone} onChange={(e) => setEditForm({ ...editForm, primary_phone: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-sm font-medium">Website</label>
                <input type="url" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://" className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Address</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-sm font-medium">Address Line 1</label>
                <input type="text" value={editForm.address_line_1} onChange={(e) => setEditForm({ ...editForm, address_line_1: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-sm font-medium">Address Line 2</label>
                <input type="text" value={editForm.address_line_2} onChange={(e) => setEditForm({ ...editForm, address_line_2: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">City</label>
                <input type="text" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">State / County</label>
                <input type="text" value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Postal Code</label>
                <input type="text" value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Country</label>
                <input type="text" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
